import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, MessageCircle, Send, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

function PracticePartners() {
  const [userId] = useState(localStorage.getItem('workbook_user_id'));
  const [userName] = useState(localStorage.getItem('workbook_user_name'));
  const [partners, setPartners] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [partnerRequest, setPartnerRequest] = useState({ looking_for: '', available_times: '' });

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  const loadData = async () => {
    try {
      // Load available partners
      const partnersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/find-partners`);
      if (partnersRes.ok) {
        const data = await partnersRes.json();
        setPartners(data.users.filter(u => u.id !== userId));
      }

      // Load partner requests
      const requestsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner-requests`);
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data.requests);
      }

      // Load messages
      const messagesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/${userId}`);
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const sendMessage = async () => {
    if (!selectedPartner || !newMessage.trim()) {
      toast.error('Please select a partner and write a message');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_user_id: userId,
          to_user_id: selectedPartner.id,
          content: newMessage
        })
      });

      if (!response.ok) throw new Error('Failed to send');

      toast.success('Message sent!');
      setNewMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const postRequest = async () => {
    if (!partnerRequest.looking_for) {
      toast.error("Please describe what you're looking for");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          looking_for: partnerRequest.looking_for,
          available_times: partnerRequest.available_times
        })
      });

      if (!response.ok) throw new Error('Failed to post');

      toast.success('Partner request posted!');
      setPartnerRequest({ looking_for: '', available_times: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to post request');
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h2 className="text-3xl font-bold mb-3 flex items-center gap-2">
          <Users className="w-8 h-8" />
          Practice Partners & Community
        </h2>
        <p className="text-lg leading-relaxed">
          <strong>NVC is learned best together.</strong> Connect with other learners to practice exercises, share insights, and support each other's journey.
        </p>
      </div>

      <Tabs defaultValue="find" className="w-full">
        <TabsList>
          <TabsTrigger value="find">Find Partners</TabsTrigger>
          <TabsTrigger value="requests">Partner Requests</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
        </TabsList>

        {/* Find Partners */}
        <TabsContent value="find" className="space-y-6">
          <Card style={{ borderRadius: 0 }}>
            <CardHeader>
              <CardTitle>Available Practice Partners</CardTitle>
            </CardHeader>
            <CardContent>
              {partners.length === 0 ? (
                <p className="text-gray-600">No other users yet. Share the workbook with friends!</p>
              ) : (
                <div className="space-y-3">
                  {partners.map(partner => (
                    <div key={partner.id} className="flex justify-between items-center p-4 border-2 border-gray-200 hover:border-black transition-colors">
                      <div>
                        <p className="font-bold text-lg">{partner.name}</p>
                        <p className="text-sm text-gray-600">{partner.exercises_completed} exercises completed</p>
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedPartner(partner);
                          document.querySelector('[value="messages"]')?.click();
                        }}
                        className="bg-black text-white hover:bg-black/90"
                        style={{ borderRadius: 0 }}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partner Requests */}
        <TabsContent value="requests" className="space-y-6">
          <Card style={{ borderRadius: 0 }}>
            <CardHeader>
              <CardTitle>Post a Partner Request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>What I'm looking for:</Label>
                <Input
                  placeholder="e.g., Practice partner for weekly exercises, accountability buddy..."
                  value={partnerRequest.looking_for}
                  onChange={(e) => setPartnerRequest({...partnerRequest, looking_for: e.target.value})}
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div className="space-y-2">
                <Label>Available times (optional):</Label>
                <Input
                  placeholder="e.g., Tuesday evenings, weekends..."
                  value={partnerRequest.available_times}
                  onChange={(e) => setPartnerRequest({...partnerRequest, available_times: e.target.value})}
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <Button onClick={postRequest} className="bg-black text-white hover:bg-black/90" style={{ borderRadius: 0 }}>
                <UserPlus className="w-4 h-4 mr-2" />
                Post Request
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <h3 className="text-xl font-bold">Recent Requests</h3>
            {requests.length === 0 ? (
              <p className="text-gray-600">No requests yet. Be the first!</p>
            ) : (
              requests.map(req => (
                <Card key={req.id} style={{ borderRadius: 0 }}>
                  <CardContent className="p-4">
                    <p className="font-bold">{req.user?.name}</p>
                    <p className="text-base mt-2">{req.looking_for}</p>
                    {req.available_times && (
                      <p className="text-sm text-gray-600 mt-1">Available: {req.available_times}</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages" className="space-y-6">
          {selectedPartner && (
            <Card style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle>Message {selectedPartner.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Share your practice insights, ask questions, coordinate practice times..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="min-h-[120px] border-black"
                  style={{ borderRadius: 0 }}
                />
                <Button onClick={sendMessage} className="bg-black text-white hover:bg-black/90" style={{ borderRadius: 0 }}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </CardContent>
            </Card>
          )}

          <div>
            <h3 className="text-xl font-bold mb-4">Your Conversations</h3>
            {messages.length === 0 ? (
              <p className="text-gray-600">No messages yet. Find a practice partner above!</p>
            ) : (
              <div className="space-y-3">
                {messages.map(msg => (
                  <Card key={msg.id} style={{ borderRadius: 0 }}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-bold">
                          {msg.from_user?.id === userId ? `To: ${msg.to_user?.name}` : `From: ${msg.from_user?.name}`}
                        </p>
                        <p className="text-xs text-gray-600">{new Date(msg.created_at).toLocaleDateString()}</p>
                      </div>
                      <p className="text-base">{msg.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default PracticePartners;
